/**
 * 資料セクションをテンプレ化するダイアログ。
 * 名前 + 説明 を入力して `documentSectionTemplates/{id}` に保存。
 */
import type { DocumentSection } from '@goenhub/shared';
import { Bookmark, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useSaveDocumentSectionTemplate } from '@/hooks/useDocumentSectionTemplates';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section: DocumentSection | null;
  /** UI 表示用のセクション label */
  sectionLabel?: string;
}

export function DocumentSaveTemplateDialog({
  open,
  onOpenChange,
  section,
  sectionLabel,
}: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const save = useSaveDocumentSectionTemplate();

  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
    }
  }, [open]);

  const handleSave = async () => {
    if (!section) return;
    if (!name.trim()) {
      toast.error('テンプレ名を入力してください');
      return;
    }
    try {
      await save.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        section,
      });
      toast.success('テンプレートとして保存しました');
      onOpenChange(false);
    } catch (e) {
      toast.error(`保存失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && save.isPending) return;
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bookmark className="size-4 text-[var(--accent)]" />
            セクションをテンプレ化
          </DialogTitle>
          <DialogDescription>
            {sectionLabel
              ? `「${sectionLabel}」の内容を再利用可能なテンプレートとして保存します。`
              : 'このセクションの内容を再利用可能なテンプレートとして保存します。'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-xs">
          <div className="space-y-1">
            <Label htmlFor="tpl-name">テンプレ名 (必須)</Label>
            <Input
              id="tpl-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: コンタクト CTA・メール診断付き"
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="tpl-desc">説明 (任意)</Label>
            <Textarea
              id="tpl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="例: 資料末尾用、診断 + 問い合わせの 2 ボタン構成"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={save.isPending}>
            キャンセル
          </Button>
          <Button onClick={handleSave} disabled={save.isPending || !name.trim()}>
            {save.isPending ? (
              <>
                <Loader2 className="size-3.5 mr-1 animate-spin" />
                保存中...
              </>
            ) : (
              <>
                <Bookmark className="size-3.5 mr-1" />
                テンプレを保存
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
